import React, { useState } from "react";
import Link from "next/link";
import { FaInstagram } from "react-icons/fa";

const Navbar = ({ websiteData }) => {
  const [activeLink, setActiveLink] = useState("Home");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openSubMenus, setOpenSubMenus] = useState({});

  const navItems = websiteData?.menus?.Nav_Menu?.items || [];
  const visibleItems = navItems.length ? navItems.slice(0, 7) : [];
  const moreItems = navItems.length ? navItems.slice(7) : [];

  const handleLinkClick = (link) => {
    setActiveLink(link);
    setMobileMenuOpen(false);
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen((prev) => !prev);
  };

  const toggleSubmenu = (title, e) => {
    e.stopPropagation();
    setOpenSubMenus((prev) => ({
      ...prev,
      [title]: !prev[title],
    }));
  };

  const handleSubItemClick = (subItemTitle) => {
    setActiveLink(subItemTitle);
    setMobileMenuOpen(false);
  };

  const ChevronDown = () => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      className="h-3 w-3 ml-1"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M19 9l-7 7-7-7"
      />
    </svg>
  );

  const ChevronRight = () => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      className="h-3 w-3 ml-2"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M9 5l7 7-7 7"
      />
    </svg>
  );

  const socialLinks = websiteData?.website?.socialLinks;

  return (
    <nav className="w-full relative z-20 bg-transparent shadow-sm">
      <button
        onClick={toggleMobileMenu}
        className="md:hidden p-4 flex items-center justify-center ml-auto"
        aria-label="Toggle navigation menu"
        aria-expanded={mobileMenuOpen}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-6 w-6 text-[#334821]"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M4 6h16M4 12h16M4 18h16"
          />
        </svg>
      </button>

      {mobileMenuOpen && (
        <div className="md:hidden bg-white shadow-lg">
          <div className="px-4 py-2 space-y-4">
            <div className="space-y-2">
              {navItems.map((item) => (
                <div key={item.title} className="py-2">
                  <div className="flex items-center justify-between">
                    <Link
                      href={item?.url || "#"}
                      className={`text-base font-medium ${
                        activeLink === item.title
                          ? "text-[#334821] font-bold"
                          : "text-gray-800"
                      }`}
                      onClick={() => handleLinkClick(item.title)}
                    >
                      {item.title}
                    </Link>
                    {item.subItems && (
                      <button
                        onClick={(e) => toggleSubmenu(item.title, e)}
                        className="p-1 rounded-full hover:bg-gray-100"
                        aria-label={`Toggle ${item.title} submenu`}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className={`h-4 w-4 transition-transform ${
                            openSubMenus[item.title] ? "rotate-180" : ""
                          }`}
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 9l-7 7-7-7"
                          />
                        </svg>
                      </button>
                    )}
                  </div>

                  {item.subItems && openSubMenus[item.title] && (
                    <div className="pl-4 mt-2 space-y-2 border-l border-gray-200">
                      {item.subItems.map((subItem) => (
                        <Link
                          key={subItem.title}
                          href={subItem.url || "#"}
                          className="block py-1 text-sm text-gray-700 hover:text-[#334821]"
                          onClick={() => handleSubItemClick(subItem.title)}
                        >
                          {subItem.title}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div className="pt-3 border-t border-gray-200">
              <div className="flex space-x-3">
                {socialLinks?.facebook && (
                  <Link
                    href={socialLinks.facebook}
                    className="w-8 h-8 bg-[#1877F2] rounded-full flex items-center justify-center"
                    aria-label="Facebook"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-4 w-4 text-white"
                      viewBox="0 0 320 512"
                      fill="currentColor"
                    >
                      <path d="M279.14 288l14.22-92.66h-88.91v-60.13c0-25.35 12.42-50.06 52.24-50.06h40.42V6.26S260.43 0 225.36 0c-73.22 0-121.08 44.38-121.08 124.72v70.62H22.89V288h81.39v224h100.17V288z" />
                    </svg>
                  </Link>
                )}
                {socialLinks?.linkedIn && (
                  <Link
                    href={socialLinks.linkedIn}
                    className="w-8 h-8 bg-[#0A66C2] rounded-full flex items-center justify-center"
                    aria-label="LinkedIn"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-4 w-4 text-white"
                      viewBox="0 0 448 512"
                      fill="currentColor"
                    >
                      <path d="M100.28 448H7.4V148.9h92.88zM53.79 108.1C24.09 108.1 0 83.5 0 53.8a53.79 53.79 0 0 1 107.58 0c0 29.7-24.1 54.3-53.79 54.3zM447.9 448h-92.68V302.4c0-34.7-.7-79.2-48.29-79.2-48.29 0-55.69 37.7-55.69 76.7V448h-92.78V148.9h89.08v40.8h1.3c12.4-23.5 42.69-48.3 87.88-48.3 94 0 111.28 61.9 111.28 142.3V448z" />
                    </svg>
                  </Link>
                )}
                {socialLinks?.instagram && (
                  <Link
                    href={socialLinks.instagram}
                    className="w-8 h-8 rounded-full flex items-center justify-center bg-gradient-to-tr from-yellow-400 via-pink-500 to-purple-600"
                    aria-label="Instagram"
                  >
                    <FaInstagram className="text-white" />
                  </Link>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="hidden md:block">
        <div className="container mx-auto flex items-center justify-between gap-6">
          <div className="flex items-center gap-2 lg:gap-4">
            {visibleItems.map((item) => (
              <div key={item.title} className="relative group">
                <div className="py-4">
                  <Link
                    href={item?.url || "#"}
                    onClick={(e) => {
                      if (!item.url) e.preventDefault();
                      handleLinkClick(item.title);
                    }}
                    className={`inline-flex items-center text-base lg:text-lg gap-2 relative text-[#334821] font-medium ${
                      activeLink === item.title
                        ? "font-bold"
                        : "hover:text-[#334821]"
                    }`}
                  >
                    {item.title}
                    {item.subItems && <ChevronDown />}
                  </Link>
                </div>

                {item.subItems && (
                  <div className="absolute left-0 top-full w-56 sm:w-64 bg-white shadow-xl rounded-md py-1 opacity-0 invisible group-hover:visible group-hover:opacity-100 transition-all">
                    {item.subItems.map((subItem) => (
                      <Link
                        key={subItem.title}
                        href={subItem.url || "#"}
                        className="block px-4 py-3 text-[#334821] hover:bg-[#f1bb62]"
                        onClick={() => handleSubItemClick(subItem.title)}
                      >
                        {subItem.title}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}

            {moreItems.length > 0 && (
              <div className="relative group">
                <div className="py-4 cursor-pointer text-[#334821] text-base lg:text-lg font-medium flex items-center whitespace-nowrap">
                  More
                  <ChevronDown />
                </div>

                <div className="absolute left-0 top-full w-56 sm:w-64 bg-white shadow-xl rounded-md py-1 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all">
                  {moreItems.map((item) => (
                    <div key={item.title} className="relative group/item">
                      <Link
                        href={item?.url || "#"}
                        className="flex items-center justify-between px-4 py-3 text-[#334821] hover:bg-[#f1bb62]"
                      >
                        {item.title}
                        {item.subItems && <ChevronRight />}
                      </Link>

                      {item.subItems && (
                        <div className="absolute left-full top-0 w-48 bg-white shadow-xl rounded-md py-1 opacity-0 invisible group-hover/item:opacity-100 group-hover/item:visible transition-all">
                          {item.subItems.map((subItem) => (
                            <Link
                              key={subItem.title}
                              href={subItem.url || "#"}
                              className="block px-4 py-3 text-[#334821] hover:bg-[#f1bb62]"
                              onClick={() => handleSubItemClick(subItem.title)}
                            >
                              {subItem.title}
                            </Link>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="flex items-center gap-3">
            {socialLinks?.facebook && (
              <Link
                href={socialLinks.facebook}
                className="w-8 h-8 bg-[#1877F2] rounded-full flex items-center justify-center"
                aria-label="Facebook"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-4 w-4 text-white"
                  viewBox="0 0 320 512"
                  fill="currentColor"
                >
                  <path d="M279.14 288l14.22-92.66h-88.91v-60.13c0-25.35 12.42-50.06 52.24-50.06h40.42V6.26S260.43 0 225.36 0c-73.22 0-121.08 44.38-121.08 124.72v70.62H22.89V288h81.39v224h100.17V288z" />
                </svg>
              </Link>
            )}

            {socialLinks?.linkedIn && (
              <Link
                href={socialLinks.linkedIn}
                className="w-8 h-8 bg-[#0A66C2] rounded-full flex items-center justify-center"
                aria-label="LinkedIn"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-4 w-4 text-white"
                  viewBox="0 0 448 512"
                  fill="currentColor"
                >
                  <path d="M100.28 448H7.4V148.9h92.88zM53.79 108.1C24.09 108.1 0 83.5 0 53.8a53.79 53.79 0 0 1 107.58 0c0 29.7-24.1 54.3-53.79 54.3zM447.9 448h-92.68V302.4c0-34.7-.7-79.2-48.29-79.2-48.29 0-55.69 37.7-55.69 76.7V448h-92.78V148.9h89.08v40.8h1.3c12.4-23.5 42.69-48.3 87.88-48.3 94 0 111.28 61.9 111.28 142.3V448z" />
                </svg>
              </Link>
            )}

            {socialLinks?.instagram && (
              <Link
                href={socialLinks.instagram}
                className="w-8 h-8 rounded-full flex items-center justify-center bg-gradient-to-tr from-yellow-400 via-pink-500 to-purple-600"
                aria-label="Instagram"
              >
                <FaInstagram className="text-white" />
              </Link>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
